/* Premier Courses — hero state picker / mini course wizard.
   Step 1: CA/FL state cards. When the page carries the guided pricing module
   ([data-pc-pricing] decks), the picker continues with the same follow-up
   questions as the on-page guide — career stage, then agent/broker — derived
   from the actual course catalog (a question is skipped when a state has only
   one option). Every answer broadcasts on the pc:tabs-sync bus so the pricing
   guide + FAQ answer along. Completing the questions arms the CTA ("See your
   Florida courses") with that path's course page (window.PCRoutes, from
   pc-routes.js) — the visitor is routed when they click it; when no route is
   defined the CTA falls back to scrolling to #courses.
   Without an on-page catalog (legacy pages), picking a state arms the CTA
   with that state's page, as before. */
(function () {
  'use strict';

  var SYNC_EVENT = 'pc:tabs-sync';
  var STAGE_ORDER = ['pre', 'post', 'ce', 'react', 'exam'];
  var COPY = {
    labels: {
      state: 'Step 1 · Where are you getting licensed?',
      stage: 'Step 2 · Where are you in your career?',
      role: 'Step 3 · Are you an agent or a broker?'
    },
    stage: {
      pre: ['Getting licensed', 'Pre-licensing education'],
      post: ['Just passed my exam', 'Post-licensing · first renewal'],
      ce: ['Renewing my license', 'Continuing education'],
      react: ['Reactivating my license', '28-hour reactivation course'],
      exam: ['Preparing for the exam', 'Optional exam prep & practice']
    },
    role: {
      agent: ['Agent', 'Sales associate'],
      broker: ['Broker', 'Licensed broker']
    }
  };

  function uniq(arr) {
    return arr.filter(function (v, i) { return arr.indexOf(v) === i; });
  }

  function initPicker(root) {
    if (root.dataset.pcInit === '1') return;
    root.dataset.pcInit = '1';

    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-pc-state-card]'));
    var cta = root.querySelector('[data-pc-state-cta]');
    if (!cards.length || !cta) return;
    var autoRoute = root.hasAttribute('data-pc-sp-autoroute');
    var navigating = false;

    var ctaLabel = cta.querySelector('[data-pc-cta-label]') || cta;
    var idleLabel = cta.getAttribute('data-label-idle') || 'Select a state to continue';
    var label = root.querySelector('[data-pc-sp-label]');
    var backBtn = root.querySelector('[data-pc-sp-back]');
    var steps = {
      state: root.querySelector('[data-pc-sp-step="state"]'),
      stage: root.querySelector('[data-pc-sp-step="stage"]'),
      role: root.querySelector('[data-pc-sp-step="role"]')
    };

    // The on-page guided catalog (if present) defines the follow-up questions.
    var pricingRoot = document.querySelector('[data-pc-pricing]');
    var decks = pricingRoot
      ? Array.prototype.slice.call(pricingRoot.querySelectorAll('[data-ppc-deck]'))
      : [];
    var wizard = decks.length > 0 && steps.stage && steps.role;

    var sel = { state: null, stage: null, role: null };

    function stagesFor(state) {
      var found = uniq(decks
        .filter(function (d) { return d.dataset.state === state; })
        .map(function (d) { return d.dataset.stage; }));
      return STAGE_ORDER.filter(function (s) { return found.indexOf(s) !== -1; });
    }

    function rolesFor(state, stage) {
      // A shared deck (role "both") serves agents and brokers alike, but their
      // destination pages differ — so it still offers both answers.
      var roles = [];
      uniq(decks
        .filter(function (d) { return d.dataset.state === state && d.dataset.stage === stage; })
        .map(function (d) { return d.dataset.role; }))
        .forEach(function (r) {
          if (r === 'both') { roles.push('agent', 'broker'); }
          else if (r === 'agent' || r === 'broker') { roles.push(r); }
        });
      return uniq(roles);
    }

    function routeFor() {
      var routes = window.PCRoutes || {};
      return routes[sel.state + '-' + sel.stage + '-' + sel.role] ||
        routes[sel.state + '-' + sel.stage + '-agent'] || null;
    }

    function broadcast(scope, value) {
      document.dispatchEvent(new CustomEvent(SYNC_EVENT, {
        detail: { scope: scope, value: value, source: root }
      }));
    }

    function disableCta(text) {
      cta.setAttribute('aria-disabled', 'true');
      delete cta.dataset.enabled;
      if (ctaLabel) ctaLabel.textContent = text || idleLabel;
    }

    function enableCta(text, href) {
      cta.setAttribute('href', href);
      cta.removeAttribute('aria-disabled');
      cta.dataset.enabled = 'true';
      if (ctaLabel) ctaLabel.textContent = text;
    }

    function showStep(name) {
      Object.keys(steps).forEach(function (k) {
        if (steps[k]) steps[k].hidden = (k !== name);
      });
      if (label) label.textContent = COPY.labels[name];
      if (backBtn) backBtn.hidden = (name === 'state');
    }

    function buildOptions(container, kind, values, onPick) {
      container.innerHTML = '';
      values.forEach(function (v) {
        var copy = COPY[kind][v] || [v, ''];
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'pc-sp-option';
        btn.setAttribute('role', 'radio');
        btn.setAttribute('aria-checked', 'false');
        var t = document.createElement('span');
        t.className = 'pc-sp-option__title';
        t.textContent = copy[0];
        btn.appendChild(t);
        if (copy[1]) {
          var s = document.createElement('span');
          s.className = 'pc-sp-option__sub';
          s.textContent = copy[1];
          btn.appendChild(s);
        }
        btn.addEventListener('click', function () {
          Array.prototype.slice.call(container.children).forEach(function (c) {
            c.setAttribute('aria-checked', c === btn ? 'true' : 'false');
          });
          onPick(v);
        });
        container.appendChild(btn);
      });
    }

    function finish() {
      var stateName = sel.state === 'CA' ? 'California' : sel.state === 'FL' ? 'Florida' : 'your';
      var dest = routeFor();
      if (dest && dest !== window.location.pathname) {
        enableCta('See your ' + stateName + ' courses', dest);
        // Auto-route (team feedback 07-21): the final answer routes directly —
        // no extra "See your courses" click. finish() only runs off user picks,
        // so this never fires on load/sync. 900ms beat with the "Taking you
        // to…" line so the selection is readable before navigation; the funnel
        // page's read-back strip then shows the choices.
        if (autoRoute && !navigating) {
          navigating = true;
          if (ctaLabel) ctaLabel.textContent = 'Taking you to your ' + stateName + ' courses…';
          setTimeout(function () { window.location.assign(dest); }, 900);
        }
      } else {
        enableCta('See your ' + stateName + ' courses', '#courses');
      }
    }

    function askRole() {
      var roles = rolesFor(sel.state, sel.stage);
      if (roles.length < 2) {
        if (roles.length === 1) { sel.role = roles[0]; broadcast('role', roles[0]); }
        finish();
        return;
      }
      buildOptions(steps.role, 'role', roles, function (v) {
        sel.role = v;
        broadcast('role', v);
        finish();
      });
      showStep('role');
      disableCta('Select an option to continue');
    }

    function askStage() {
      var stages = stagesFor(sel.state);
      if (stages.length < 2) {
        if (stages.length === 1) { sel.stage = stages[0]; broadcast('stage', stages[0]); }
        askRole();
        return;
      }
      buildOptions(steps.stage, 'stage', stages, function (v) {
        sel.stage = v;
        broadcast('stage', v);
        askRole();
      });
      showStep('stage');
      disableCta('Select an option to continue');
    }

    function select(card, silent, advance) {
      cards.forEach(function (c) {
        var on = c === card;
        c.setAttribute('aria-checked', on ? 'true' : 'false');
        c.setAttribute('tabindex', on ? '0' : '-1');
        if (on) { c.dataset.on = 'true'; } else { delete c.dataset.on; }
      });

      var state = card.getAttribute('data-state');
      if (!silent && state) { broadcast('state', state); }

      if (silent) return; // external sync: mirror the card, don't advance steps

      if (!wizard) {
        var href = card.getAttribute('data-state-url');
        enableCta(card.getAttribute('data-state-cta') || 'Continue', href);
        return;
      }
      if (advance === false) return; // arrow-key browsing: mark + broadcast only
      sel.state = state;
      sel.stage = null;
      sel.role = null;
      askStage();
    }

    if (backBtn) {
      backBtn.addEventListener('click', function () {
        if (!steps.role.hidden) {
          askStage();
          if (stagesFor(sel.state).length < 2) { showStep('state'); disableCta(); }
        } else {
          showStep('state');
          disableCta();
        }
      });
    }

    document.addEventListener(SYNC_EVENT, function (e) {
      if (e.detail.source === root || e.detail.scope !== 'state') return;
      var match = cards.filter(function (c) {
        return c.getAttribute('data-state') === e.detail.value;
      })[0];
      if (match && match.dataset.on !== 'true') { select(match, true); }
    });

    cards.forEach(function (card, i) {
      card.addEventListener('click', function () { select(card); });

      // Roving-tabindex arrow-key navigation for the radio group.
      card.addEventListener('keydown', function (e) {
        var key = e.key;
        var next = null;
        if (key === 'ArrowRight' || key === 'ArrowDown') {
          next = cards[(i + 1) % cards.length];
        } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
          next = cards[(i - 1 + cards.length) % cards.length];
        } else if (key === ' ' || key === 'Enter') {
          e.preventDefault();
          select(card);
          return;
        }
        if (next) {
          e.preventDefault();
          select(next, false, false);
          next.focus();
        }
      });
    });

    // Block navigation while the CTA is disabled (no path chosen yet).
    cta.addEventListener('click', function (e) {
      if (cta.dataset.enabled !== 'true') {
        e.preventDefault();
      }
    });
    disableCta();
  }

  function initAll(scope) {
    var ctx = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.slice
      .call(ctx.querySelectorAll('[data-pc-statepicker]'))
      .forEach(initPicker);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  // Re-init when re-rendered inside the Shopify theme editor.
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
